import React, { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { useSeo, getContentPage, type ContentPage, type SiteSettings } from "@/lib/publicContent";

export default function ContentPageView({ pageKey }: { pageKey: string }) {
  useOutletContext<SiteSettings | null>();
  const navigate = useNavigate();
  const [page, setPage] = useState<ContentPage | null | undefined>(undefined);

  useEffect(() => {
    let mounted = true;
    setPage(undefined);
    getContentPage(pageKey).then((p) => {
      if (!mounted) return;
      if (!p) {
        navigate("/", { replace: true });
        return;
      }
      setPage(p);
    });
    return () => {
      mounted = false;
    };
  }, [pageKey, navigate]);

  useSeo({
    title: page?.title ? `${page.title} — Bonker` : "Bonker",
    description: page?.description || "Sneaker personalizzate dipinte a mano.",
    image: page?.images?.[0] ? mediaUrl(page.images[0]) : undefined,
  });

  if (page === undefined) {
    return (
      <div className="py-32 flex justify-center" data-testid="content-page-loading">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!page) return null;

  const images = page.images || [];
  const btnUrl = (page.button_url || "").trim();
  const btnExternal = /^https?:/i.test(btnUrl);
  const btnLabel = page.button_text || "Scrivimi";
  const waMsg = (page.whatsapp_message || "").trim();
  const isWhatsapp = /wa\.me|whatsapp\.com/i.test(btnUrl);
  const finalHref =
    btnUrl && waMsg && isWhatsapp
      ? btnUrl + (btnUrl.includes("?") ? "&" : "?") + "text=" + encodeURIComponent(waMsg)
      : btnUrl;

  return (
    <div data-testid={`content-page-${pageKey}`}>
      <section className="relative overflow-hidden bonker-noise" aria-labelledby="cp-heading">
        <div className="absolute -top-40 -right-40 h-[32rem] w-[32rem] rounded-full bg-primary/20 blur-[120px] pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 h-[28rem] w-[28rem] rounded-full bg-secondary/10 blur-[120px] pointer-events-none" />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto px-5 md:px-10 py-20 md:py-28 relative z-10 text-center"
        >
          {page.eyebrow && (
            <span className="inline-block text-xs uppercase tracking-[0.25em] font-bold text-secondary mb-5" data-testid="content-page-eyebrow">
              {page.eyebrow}
            </span>
          )}
          <h1 id="cp-heading" data-testid="content-page-title" className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter">
            {page.title}
          </h1>
          {page.description && (
            <p className="text-base md:text-lg text-muted-foreground mt-7 leading-relaxed whitespace-pre-line" data-testid="content-page-description">
              {page.description}
            </p>
          )}
          {btnUrl &&
            (btnExternal ? (
              <a href={finalHref} target="_blank" rel="noreferrer" data-testid="content-page-cta" className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold uppercase tracking-[0.12em] text-sm px-8 py-4 mt-9 hover:-translate-y-[2px] transition-transform">
                {btnLabel} <ArrowRight className="h-4 w-4" />
              </a>
            ) : (
              <Link to={btnUrl} data-testid="content-page-cta" className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold uppercase tracking-[0.12em] text-sm px-8 py-4 mt-9 hover:-translate-y-[2px] transition-transform">
                {btnLabel} <ArrowRight className="h-4 w-4" />
              </Link>
            ))}
        </motion.div>
      </section>

      {images.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 md:px-10 pb-20 md:pb-28" data-testid="content-page-gallery">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {images.map((src, i) => (
              <div key={i} className="border border-white/10 bg-card overflow-hidden aspect-[4/5]">
                <img src={mediaUrl(src)} alt={`${page.title} ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
